// Authentication service for Turn Connect API
//
// This module used to be a 5,400-line god class. The admin surface now lives
// in per-domain modules under ./adminApi/, composed back into the same
// `adminService` export so the 90+ importers keep working unchanged. The
// type exports below are part of the module's public API — keep them here.
import { webApiClient } from './apiClient';
import type { User, Location, Department, DashboardDepartment, DashboardSection, LocationDashboardResponse } from '@turn-connect/shared';
import { AnalyticsService, normalizeAuthUser } from '@turn-connect/shared';
import { locationsDepartmentsAdmin } from './adminApi/locationsDepartments';
import { catalogAdmin } from './adminApi/catalog';
import { importExportAdmin } from './adminApi/importExport';
import { usersRolesAdmin } from './adminApi/usersRoles';
import { accountsContextAdmin } from './adminApi/accountsContext';
import { invoicingBillingAdmin } from './adminApi/invoicingBilling';
import { miscOpsAdmin } from './adminApi/miscOps';
import { reportsAdmin } from './adminApi/reports';
import { workTypesAdmin } from './adminApi/workTypes';
import { workOrderPoliciesAdmin } from './adminApi/workOrderPolicies';
import { policyDeviationsAdmin } from './adminApi/policyDeviations';
import { locationGroupsAdmin } from './adminApi/locationGroups';
import { locationGroupRolesAdmin } from './adminApi/locationGroupRoles';
import { sessionPolicyOverridesAdmin } from './adminApi/sessionPolicyOverrides';
import { locationDefaultsAdmin } from './adminApi/locationDefaults';

export interface LoginRequest {
  // Either email or phone_number is required for login
  email?: string;
  phone_number?: string;
  password: string;
}

// Re-export for backwards compatibility
export type { User, Location, Department, DashboardDepartment, DashboardSection, LocationDashboardResponse };

export interface ImportRowResult {
  row_number: number;
  service_name: string;
  department_name?: string;
  status: 'success' | 'error' | 'updated' | 'would_create' | 'would_update';
  error_message?: string;
  error_code?: string; // Machine-readable error code (e.g., "SECTION_NOT_FOUND")
  service_id?: string;
  department_id?: string;
}

// Per-row outcome from POST /accounts/:account_id/locations/import
// Distinct from the service-import shape — backend returns
// services.LocationImportRowResult (see internal/domain/services/location_import_service.go).
export interface LocationImportRowResult {
  row: number;
  action: 'created' | 'updated' | 'skipped' | 'error';
  message?: string;
  id?: string;
  name?: string;
}

// Aggregate response from POST /accounts/:account_id/locations/import.
export interface LocationImportResponse {
  total_rows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  results: LocationImportRowResult[];
  unknown_columns?: string[];
}

// Soft-delete conflict surfaced before an import runs. Mirrors services.ImportConflict.
// Each entry corresponds to a row whose service name matches an existing soft-deleted service
// (or one attached to a soft-deleted department). Resubmitting the import with
// confirm_restore_all=true acknowledges the list and lets the backend undelete + repoint.
export interface ImportConflict {
  row_number: number;
  service_name: string;
  existing_service_id: string;
  service_deleted: boolean;
  existing_department_id?: string;
  existing_department_name?: string;
  existing_department_deleted: boolean;
  target_department_name?: string;
}

export interface ImportResponse {
  total_rows: number;
  success_count: number;
  error_count: number;
  updated_count: number;
  departments_created: number;
  results: ImportRowResult[];
  // validation_failed: Pre-flight validation failed, no changes made
  // needs_resolution: Soft-delete conflicts must be acknowledged via confirm_restore_all
  status: 'completed' | 'completed_with_errors' | 'failed' | 'validation_failed' | 'needs_resolution' | 'no_data';
  conflicts?: ImportConflict[];
}

// Terms document from login response
export interface LoginTermsDocument {
  id: string;
  type: string;
  version: string;
  title: string;
  content_url?: string;
  effective_date: string;
  is_active: boolean;
}

export interface LoginResponse {
  user: User;
  locations: Location[] | null;
  permissions: string[];
  message: string;
  session?: {
    expires_at: string;
  };
  // Pending terms that user needs to accept (included in login response)
  has_pending_terms?: boolean;
  pending_terms?: LoginTermsDocument[];
}

/**
 * @deprecated Role templates are being removed in favor of centralized account-level roles.
 * Use the new role_type field on roles instead.
 */
export interface RoleTemplate {
  id: string;
  name: string;
  description: string;
  account_id: string;
  auto_provision_to_locations: boolean;
  source_config_key?: string;
  permissions: Array<{
    id: string;
    key: string;
    label: string;
    category?: string;
    sort_order?: number;
    scope?: string;
  }>;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

// Invitation types
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'declined' | 'revoked';

export interface InvitationRoleAssignment {
  role_id: string;
  role_name?: string;
  location_id?: string;
  location_name?: string;
}

export interface Invitation {
  id: string;
  email?: string;
  phone_number?: string;
  first_name: string;
  last_name: string;
  account_id: string;
  account_name?: string;
  invited_by: string;
  invited_by_name?: string;
  role_assignments: InvitationRoleAssignment[];
  user_metadata?: Record<string, unknown>;
  status: InvitationStatus;
  expires_at: string;
  sent_at?: string;
  sent_via?: 'email' | 'sms';
  accepted_at?: string;
  accepted_by_user_id?: string;
  declined_at?: string;
  revoked_at?: string;
  revoked_by?: string;
  created_at: string;
  updated_at: string;
}

// Failed VIN Scan types
export interface FailedVINScanTierAttempt {
  tier: string;
  success: boolean;
  duration_ms: number;
  error?: string;
}

export interface FailedVINScan {
  id: string;
  account_id?: string;
  user_id?: string;
  user_email?: string;
  image_storage_key: string;
  image_hash: string;
  content_type: string;
  image_size_bytes: number;
  error_message: string;
  error_code?: string;
  tier_preference?: string;
  tiers_attempted?: FailedVINScanTierAttempt[];
  created_at: string;
  expires_at: string;
}

export interface FailedVINScanStats {
  total_count: number;
  by_error_code: Record<string, number>;
  oldest?: string;
  newest?: string;
}

export interface FailedVINScanListResponse {
  data: FailedVINScan[];
  total: number;
  limit: number;
  offset: number;
}

class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // The central client attaches Accept, X-Requested-With, and the CSRF
    // double-submit headers (X-CSRF-Token / X-XSRF-TOKEN from cookies) —
    // exactly what the hand-rolled fetch here used to build — and sends
    // credentials: 'include' for the JWT cookie.
    const res = await webApiClient.post<LoginResponse>('/auth/login', credentials);

    if (!res.success) {
      // No status means the request never reached the server (network error).
      if (res.status === undefined) {
        throw new Error(`Unable to reach server. Please check your connection and try again. (${res.error || 'Network error'})`);
      }

      const status = res.status;
      const payload: any = res.errorBody ?? null;

      // Forced-reset gate: backend returns 403 with this code and a
      // short-lived reset_token. Surface it as a typed error so the
      // login screen can hand off to the reset panel without
      // confusing the user with a generic "invalid credentials".
      if (status === 403 && payload?.code === 'FORCE_PASSWORD_RESET_REQUIRED' && typeof payload?.reset_token === 'string') {
        const err: Error & { code?: string; resetToken?: string } = new Error('Password reset required before login.');
        err.code = 'FORCE_PASSWORD_RESET_REQUIRED';
        err.resetToken = payload.reset_token;
        throw err;
      }

      // Prefer structured error fields when available
      const apiMessage = payload?.error || payload?.message || '';

      // Friendly defaults by status
      let friendly = apiMessage || 'Login failed';
      if (!apiMessage) {
        if (status === 401 || status === 403) friendly = 'Invalid email/username or password.';
        else if (status === 429) friendly = 'Too many attempts. Please wait and try again shortly.';
        else if (status >= 500) friendly = 'Server error during login. Please try again.';
        else if (res.error) {
          const t = res.error.toLowerCase();
          if (t.includes('invalid') || t.includes('credential')) friendly = 'Invalid email/username or password.';
        }
      }

      // Include status for debugging, but keep message user-friendly
      throw new Error(friendly);
    }

    return res.data as LoginResponse;
  }

  // Completes the forced-reset flow with the short-lived reset_token
  // returned by login. On success the server has persisted the new
  // password and cleared the user's force_password_reset flag — caller
  // should then post regular credentials to login() to establish the
  // session normally.
  async completeForcePasswordReset(resetToken: string, newPassword: string): Promise<void> {
    const res = await webApiClient.post('/auth/force-reset-password', {
      reset_token: resetToken,
      new_password: newPassword,
    });
    if (!res.success) {
      const payload: any = res.errorBody ?? null;
      throw new Error(payload?.error || `Password reset failed (${res.status})`);
    }
  }

  async logout(): Promise<void> {
    // The client attaches the CSRF token from cookies automatically.
    const res = await webApiClient.post('/auth/logout');
    if (!res.success) {
      throw new Error('Logout failed');
    }
  }

  async getCurrentUser(): Promise<User> {
    const res = await webApiClient.get<User>('/auth/me');

    if (!res.success) {
      if (res.status === 401) {
        throw new Error('Not authenticated');
      }
      throw new Error('Failed to fetch user data');
    }

    // /auth/me is keyed on user_id; callers that read this straight (rather
    // than through useAuth's own mapping) expect a User with `id`. Without
    // this, ServiceNotesModal's "waiting on you" owner check silently
    // resolved to null for everyone.
    return normalizeAuthUser(res.data as User) as User;
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  async updateUserProfile(
    userId: string,
    updates: Partial<User> & { current_password?: string }
  ): Promise<{ user: User; credential_changed: boolean }> {
    // Align with OpenAPI: use PUT for updates (partial fields allowed)
    const res = await webApiClient.put<any>(`/users/${userId}`, updates);

    if (!res.success) {
      // Match the legacy behavior: prefer the structured `error` field from a
      // JSON body, otherwise fall back to the raw body text; always attach
      // the HTTP status — callers branch on it.
      const message =
        res.errorBody !== undefined ? res.errorBody?.error || 'Failed to update profile' : res.error || 'Failed to update profile';
      const err = new Error(message);
      (err as any).status = res.status;
      throw err;
    }

    const data = res.data;
    // Backend returns { credential_changed: true, user: {...} } when credentials changed
    // Otherwise returns the user object directly
    if (data.credential_changed) {
      return { user: data.user, credential_changed: true };
    }
    return { user: data, credential_changed: false };
  }

  async getUserById(userId: string): Promise<User> {
    const res = await webApiClient.get<User>(`/users/${userId}`);

    if (!res.success) {
      if (res.status === 401) {
        throw new Error('Not authenticated');
      }
      throw new Error(res.error || 'Failed to fetch user details');
    }

    return res.data as User;
  }
}

// Admin operations, composed from the per-domain modules in ./adminApi/.
// Each spread is a plain-function aggregate, so the composed object is
// method-for-method compatible with the old AdminService class instance
// (callers may `.bind(adminService)` — binding a plain function is a no-op).
export const adminService = {
  getApiBaseUrl: () => webApiClient.getBaseUrl(),
  ...locationsDepartmentsAdmin,
  ...catalogAdmin,
  ...importExportAdmin,
  ...usersRolesAdmin,
  ...accountsContextAdmin,
  ...invoicingBillingAdmin,
  ...miscOpsAdmin,
  ...reportsAdmin,
  ...workTypesAdmin,
  ...workOrderPoliciesAdmin,
  ...policyDeviationsAdmin,
  ...locationGroupsAdmin,
  ...locationGroupRolesAdmin,
  ...sessionPolicyOverridesAdmin,
  ...locationDefaultsAdmin,
};

// Compatibility alias for code that referenced the AdminService class type.
export type AdminService = typeof adminService;

export const authService = new AuthService();
export const analyticsService = new AnalyticsService(webApiClient);
